import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { PageHero, Section, Container, Eyebrow, H2, Lead, CTAButton, GlassCard, Orbs } from "../components/PageUI";
import { faqs, faqCategories } from "../mock";
import SEO from "../components/SEO";

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqCategories.flatMap((cat) => cat.items).map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
};

function FaqItem({ item, isOpen, onClick }) {
  return (
    <GlassCard hover={false} className="p-0 overflow-hidden">
      <button onClick={onClick} className="w-full flex items-center justify-between text-left px-6 py-5">
        <span className="font-display text-lg text-[#0e2408] font-semibold pr-4">{item.q}</span>
        <span className="w-8 h-8 rounded-full btn-gold flex items-center justify-center shrink-0">{isOpen ? <Minus size={16}/> : <Plus size={16}/>}</span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-6 pb-6 text-[15px] text-[#4b5563] leading-relaxed">{item.a}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </GlassCard>
  );
}

export default function FAQ() {
  const [openTop, setOpenTop] = useState(0);
  const [openKey, setOpenKey] = useState(null); // "catIdx-itemIdx"

  return (
    <div>
      <SEO
        title="FAQ | Masjid Suite"
        description="Answers to common questions about Masjid Suite's free software, hardware pricing, payment processing, the mobile apps, and how the platform stays free."
        path="/faq"
        jsonLd={faqJsonLd}
      />
      <PageHero
        eyebrow="FAQ"
        title="Frequently Asked"
        gradient="Questions."
        lead={<>Quick answers to common questions.</>}
      />

      <Section className="bg-section-alt overflow-hidden">
        <Container className="max-w-3xl">
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <FaqItem key={i} item={f} isOpen={openTop === i} onClick={() => setOpenTop(openTop === i ? -1 : i)} />
            ))}
          </div>
          <div className="mt-12 flex justify-center"><CTAButton to="/contact">Contact Our Team</CTAButton></div>
        </Container>
      </Section>

      <Section className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-12">
            <Eyebrow>Masjid Suite FAQ</Eyebrow>
            <H2 gradient="organized by topic">A deeper look at how Masjid Suite works,</H2>
          </div>

          <div className="space-y-14">
            {faqCategories.map((cat, ci) => (
              <div key={ci}>
                <div className="mb-5 flex items-center gap-3">
                  <span className="gold-rule" />
                  <h3 className="font-display text-2xl text-[#0e2408] font-semibold">{cat.name}</h3>
                  <span className="flex-1 h-px bg-[#e6e7eb]" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {cat.items.map((f, ii) => {
                    const key = `${ci}-${ii}`;
                    return <FaqItem key={key} item={f} isOpen={openKey === key} onClick={() => setOpenKey(openKey === key ? null : key)} />;
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 flex justify-center"><CTAButton to="/contact">Contact Our Team</CTAButton></div>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="glass-frosted rounded-[40px] p-10 md:p-14 text-center shadow-apple-lg">
            <H2 gradient="questions?">Still have</H2>
            <p className="mt-6 text-[#4b5563] text-lg">Our team is happy to walk through anything not covered here.</p>
            <div className="mt-10 flex justify-center"><CTAButton to="/contact">Get Started Free</CTAButton></div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
